import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Mail,
  MessageSquare,
  Users,
  BarChart3,
  LogOut,
  Phone,
  Calendar,
  RefreshCw,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user === false)) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  const load = async () => {
    setRefreshing(true);
    try {
      const [statsRes, contactsRes, chatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/chats`),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setChats(chatsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) load();
  }, [user]);

  const openChat = async (session_id) => {
    setSelectedChat(session_id);
    const { data } = await axios.get(`${API}/admin/chats/${session_id}`);
    setChatMessages(data);
  };

  const markRead = async (id) => {
    await axios.post(`${API}/admin/contacts/${id}/read`);
    load();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (loading || !user || user === false) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-[#475569]">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="GS" className="h-8 w-auto" />
              <div className="hidden sm:block">
                <div className="font-display font-bold text-sm text-[#0B2D5C]">GREAT SOLUTIONS US</div>
                <div className="text-[10px] text-[#475569] tracking-wider uppercase">Panel Admin</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#475569] hidden sm:block">
              {user.name || user.email}
            </span>
            <button
              onClick={load}
              disabled={refreshing}
              className="p-2 text-[#475569] hover:text-[#0B2D5C] transition-colors"
              data-testid="admin-refresh"
              aria-label="Refrescar"
            >
              <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
            </button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="rounded-sm border-[#e2e8f0]"
              data-testid="admin-logout"
            >
              <LogOut size={14} className="mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#e2e8f0] overflow-x-auto">
          {[
            { id: "stats", label: "Resumen", icon: BarChart3 },
            { id: "contacts", label: "Contactos", icon: Mail, badge: stats?.contacts_unread },
            { id: "chats", label: "Conversaciones", icon: MessageSquare, badge: stats?.chats_total },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? "border-[#0B2D5C] text-[#0B2D5C]"
                  : "border-transparent text-[#475569] hover:text-[#0f172a]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={16} />
              {t.label}
              {t.badge > 0 && (
                <span className="bg-[#C89B2C] text-[#0f172a] text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Stats */}
        {tab === "stats" && stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats">
            {[
              { label: "Total contactos", value: stats.contacts_total, icon: Mail, color: "bg-[#0B2D5C]" },
              { label: "Sin leer", value: stats.contacts_unread, icon: Mail, color: "bg-[#C89B2C]", ink: true },
              { label: "Conversaciones", value: stats.chats_total, icon: MessageSquare, color: "bg-[#0f172a]" },
              { label: "Mensajes totales", value: stats.messages_total, icon: Users, color: "bg-[#0B2D5C]" },
            ].map((s) => (
              <div key={s.label} className="bg-white p-6 border border-[#e2e8f0] rounded-sm">
                <div className={`w-10 h-10 rounded-sm ${s.color} flex items-center justify-center mb-4`}>
                  <s.icon size={18} className={s.ink ? "text-[#0f172a]" : "text-white"} strokeWidth={1.75} />
                </div>
                <div className="font-display text-3xl font-bold text-[#0f172a]">{s.value}</div>
                <div className="text-xs text-[#475569] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Contacts */}
        {tab === "contacts" && (
          <div className="bg-white border border-[#e2e8f0] rounded-sm overflow-hidden" data-testid="admin-contacts">
            {contacts.length === 0 ? (
              <div className="p-12 text-center text-[#475569]">Aún no hay contactos.</div>
            ) : (
              <div className="divide-y divide-[#e2e8f0]">
                {contacts.map((c) => (
                  <div key={c.id} className={`p-5 hover:bg-[#f8fafc] transition-colors ${!c.read ? "border-l-4 border-l-[#C89B2C]" : ""}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <span className="font-semibold text-[#0f172a]">{c.name}</span>
                          {!c.read && (
                            <span className="text-[10px] font-bold text-[#0f172a] bg-[#C89B2C] px-2 py-0.5 rounded-sm">
                              NUEVO
                            </span>
                          )}
                          {c.service && (
                            <span className="text-xs text-[#0B2D5C] bg-[#0B2D5C]/10 px-2 py-0.5 rounded-sm">
                              {c.service}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569] mb-2">
                          <a href={`mailto:${c.email}`} className="hover:text-[#0B2D5C]">
                            <Mail size={11} className="inline mr-1" />
                            {c.email}
                          </a>
                          {c.phone && (
                            <a href={`tel:${c.phone}`} className="hover:text-[#0B2D5C]">
                              <Phone size={11} className="inline mr-1" />
                              {c.phone}
                            </a>
                          )}
                          <span>
                            <Calendar size={11} className="inline mr-1" />
                            {new Date(c.created_at).toLocaleString("es")}
                          </span>
                        </div>
                        <p className="text-sm text-[#334155] leading-relaxed">{c.message}</p>
                      </div>
                      {!c.read && (
                        <Button
                          onClick={() => markRead(c.id)}
                          variant="outline"
                          size="sm"
                          className="rounded-sm text-xs"
                          data-testid={`mark-read-${c.id}`}
                        >
                          Marcar leído
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chats */}
        {tab === "chats" && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-chats">
            <div className="lg:col-span-1 bg-white border border-[#e2e8f0] rounded-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-[#e2e8f0] text-xs font-semibold text-[#475569] uppercase tracking-wider">
                Sesiones ({chats.length})
              </div>
              {chats.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#475569]">Sin conversaciones.</div>
              ) : (
                <div className="divide-y divide-[#e2e8f0] max-h-[600px] overflow-y-auto">
                  {chats.map((c) => (
                    <button
                      key={c.session_id}
                      onClick={() => openChat(c.session_id)}
                      className={`w-full text-left p-4 hover:bg-[#f8fafc] transition-colors ${
                        selectedChat === c.session_id ? "bg-[#0B2D5C]/5" : ""
                      }`}
                      data-testid={`chat-session-${c.session_id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-mono text-[#0B2D5C] truncate">
                          {c.session_id.slice(0, 12)}...
                        </span>
                        <ChevronRight size={14} className="text-[#475569]" />
                      </div>
                      <div className="text-xs text-[#334155] line-clamp-2 mb-1">{c.last_message}</div>
                      <div className="text-[10px] text-[#94a3b8]">
                        {c.count} mensajes · {new Date(c.last_at).toLocaleString("es")}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-2 bg-white border border-[#e2e8f0] rounded-sm">
              {!selectedChat ? (
                <div className="p-12 text-center text-[#475569]">
                  <MessageSquare size={40} strokeWidth={1.25} className="mx-auto mb-3 text-[#cbd5e1]" />
                  Selecciona una conversación
                </div>
              ) : (
                <div>
                  <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedChat(null)}
                        className="text-[#475569] hover:text-[#0f172a] lg:hidden"
                      >
                        <ArrowLeft size={16} />
                      </button>
                      <div>
                        <div className="text-xs text-[#475569]">Sesión</div>
                        <div className="text-sm font-mono font-semibold text-[#0B2D5C]">{selectedChat}</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 space-y-3 max-h-[560px] overflow-y-auto">
                    {chatMessages.map((m) => (
                      <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[80%] px-4 py-2.5 rounded-sm text-sm leading-relaxed whitespace-pre-wrap ${
                            m.role === "user"
                              ? "bg-[#0B2D5C] text-white"
                              : "bg-[#f8fafc] border border-[#e2e8f0] text-[#0f172a]"
                          }`}
                        >
                          {m.content}
                          <div className={`text-[10px] mt-1 ${m.role === "user" ? "text-white/60" : "text-[#94a3b8]"}`}>
                            {new Date(m.created_at).toLocaleString("es")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
